import React from 'react';
import { Link } from 'wouter';
import { ShieldCheck, Calendar, Mail, CheckCircle2, Sliders } from 'lucide-react';

interface OperationsFeatureGateProps {
    featureTitle?: string;
    featureSubtitle?: string;
    featureName?: string;
    description?: string;
    highlights?: string[];
    benefits?: string[];
    compact?: boolean;
    testId?: string;
}

export const OperationsFeatureGate: React.FC<OperationsFeatureGateProps> = ({
    featureTitle,
    featureSubtitle,
    featureName,
    description,
    highlights,
    benefits,
    compact = false,
    testId = 'operations-feature-gate',
}) => {
    const resolvedHighlights = highlights || benefits;
    const resolvedTitle = featureName || featureTitle || 'Rule-Based Dock Auto-Approval';
    const resolvedSubtitle =
        description ||
        featureSubtitle ||
        'Automated appointment evaluation, dock assignment rules, and coordinator activity logs are included with the BayWhiz Operations plan.';

    return (
        <div
            className={compact ? 'my-3' : 'max-w-3xl mx-auto my-8 px-4'}
            data-testid={testId}
        >
            <div className="bg-slate-900/90 border border-slate-700/80 rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                        <Sliders className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-semibold uppercase tracking-wider mb-3">
                            Operations Plan Feature · $199 USD/month per facility
                        </div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">
                            {resolvedTitle}
                        </h1>
                        <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed">
                            {resolvedSubtitle}
                        </p>
                    </div>
                </div>

                <div className="mt-6 bg-slate-800/60 border border-slate-700/60 rounded-xl p-5">
                    <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        {resolvedHighlights && resolvedHighlights.length > 0
                            ? `What ${resolvedTitle} Includes`
                            : 'What Rule-Based Dock Auto-Approval Includes'}
                    </h2>
                    {resolvedHighlights && resolvedHighlights.length > 0 ? (
                        <ul className="space-y-2.5 text-sm text-slate-300">
                            {resolvedHighlights.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2.5">
                                    <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="space-y-2.5 text-sm text-slate-300">
                            <li className="flex items-start gap-2.5">
                                <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                                <span>
                                    <strong className="text-white">Deterministic Multi-Check Rule Evaluation:</strong>{' '}
                                    Automatically evaluates carrier booking requests against required driver and equipment fields, linked PO presence and open status, PO due-date delivery windows, facility receiving hours, minimum advance notice hours, real-time dock door capacity, warehouse carrier trust status, and maximum pallet thresholds.
                                </span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                                <span>
                                    <strong className="text-white">Automatic Dock Door Assignment:</strong>{' '}
                                    Eligible carrier requests that pass every configured rule are automatically approved and assigned to an open dock door without manual intervention.
                                </span>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                                <span>
                                    <strong className="text-white">Coordinator Inbox & Rule Audit Trail:</strong>{' '}
                                    Filter exceptions that require human review, inspect per-rule pass/fail reasons, and configure facility-specific auto-approval thresholds.
                                </span>
                            </li>
                        </ul>
                    )}
                </div>

                <div className="mt-5 bg-emerald-950/30 border border-emerald-500/30 rounded-xl p-4 text-sm text-emerald-200">
                    <strong className="text-white">Your Starter Plan ($99 USD/month) Remains Fully Active:</strong>{' '}
                    Unlimited docks and warehouse users, public carrier booking requests, manual appointment approvals, time revisions, dock check-in/out, and live schedule operations continue to work from your Schedule view.
                </div>

                <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-xs sm:text-sm text-slate-400">
                        To upgrade this facility to <strong className="text-slate-200">Operations ($199 USD/month)</strong>, contact{' '}
                        <a
                            href="mailto:support@baywhiz.com?subject=BayWhiz%20Operations%20Plan%20Upgrade"
                            className="text-blue-400 hover:text-blue-300 underline font-medium"
                        >
                            support@baywhiz.com
                        </a>
                        .
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Link
                            href="/schedule"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium border border-slate-700 transition-colors"
                            data-testid="operations-gate-schedule-link"
                        >
                            <Calendar className="w-4 h-4" />
                            Go to Schedule & Manual Approvals
                        </Link>
                        <a
                            href="mailto:support@baywhiz.com?subject=BayWhiz%20Operations%20Plan%20Upgrade"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold shadow-sm transition-colors"
                            data-testid="operations-gate-contact-support"
                        >
                            <Mail className="w-4 h-4" />
                            Contact support@baywhiz.com
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OperationsFeatureGate;
